import type AccessibleFeature from '@/features/users/feature-accessibility/models/AccessibleFeature';

import { useLocalStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, watch } from 'vue';

import { uniq } from 'lodash-es';

import useCurrentUserId from '@/features/authentication/current-user/useCurrentUserId';
import getAccessibleFeatures from '@/features/users/feature-accessibility/api/getAccessibleFeatures';
import ApiResultStatus from '@/shared/api/api-result/ApiResultStatus';

export default defineStore('currentUserAccessibleFeatures', () => {
  const accessibleFeatures = useLocalStorage<AccessibleFeature[]>('currentUserAccessibleFeatures', []);

  const currentUserId = useCurrentUserId();

  const loadAccessibleFeatures = async () => {
    if (currentUserId.value === null) {
      return;
    }

    const result = await getAccessibleFeatures(currentUserId.value);

    if (result.status !== ApiResultStatus.OK) {
      accessibleFeatures.value = [];

      return;
    }

    accessibleFeatures.value = uniq(result.accessibleFeatures);
  };

  watch(
    currentUserId,
    () => {
      void loadAccessibleFeatures();
    },
    {
      immediate: true,
    },
  );

  const checkIfHasAccessToFeature = computed(() => {
    const accessibleFeaturesValue = accessibleFeatures.value;

    return (feature: AccessibleFeature) => accessibleFeaturesValue.includes(feature);
  });

  return {
    checkIfHasAccessToFeature,
  };
});
